/**
 * QA Panel — CSV export of missing translation keys.
 *
 * Builds a CSV string the popup can download as a file. Two sources are
 * combined:
 *   1. Runtime-observed misses (from missingKeyTracker)
 *   2. Static coverage gaps (from coverageCalculator)
 * The runtime list is what QA actually hit while clicking around; the
 * static list catches keys that exist in EN but not in lang X even if
 * nobody navigated to a screen using them.
 */

import type { LanguageCoverage, MissingKeyRecord } from '../types';

function csvEscape(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

export interface CsvExport {
    csv: string;
    filename: string;
}

export function buildMissingKeysCsv(
    runtimeRecords: MissingKeyRecord[],
    coverage: Record<string, LanguageCoverage>
): CsvExport {
    const rows: string[] = ['language,key,source,hit_count'];

    // Runtime observations.
    for (const r of runtimeRecords) {
        rows.push(
            [csvEscape(r.lang), csvEscape(r.key), 'runtime', String(r.count)].join(',')
        );
    }

    // Static gaps from the coverage matrix.
    // Skip duplicates already reported as runtime hits.
    const runtimeSet = new Set(runtimeRecords.map((r) => `${r.lang}::${r.key}`));
    for (const lang of Object.keys(coverage)) {
        for (const key of coverage[lang].missingKeys) {
            if (runtimeSet.has(`${lang}::${key}`)) continue;
            rows.push([csvEscape(lang), csvEscape(key), 'static', '0'].join(','));
        }
    }

    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return {
        csv: rows.join('\n') + '\n',
        filename: `missing-translations-${stamp}.csv`,
    };
}
